<!--
  Form Layout Component Template
  =============================
  
  Multi-step form layout template that provides step navigation, progress tracking,
  and navigation controls. This template works in conjunction with the FormLayoutComponent
  to create a complete step-by-step form experience.
  
  Template Structure:
  - Fixed Header: Progress bar and step indicators (always visible)
  - Scrollable Content: Form content area with ng-content projection
  - Fixed Footer: Navigation buttons (previous, next, submit)
  
  Key Features:
  - Visual step progress with icons and completion indicators
  - Interactive step navigation (click to jump to completed steps)
  - Responsive navigation buttons (mobile vs desktop)
  - Loading state overlay during form operations
  - Safe area handling for mobile devices
  
  Data Binding:
  - steps: Array of form steps from parent component
  - currentStep: Currently active step identifier
  - completedSteps: Set of completed step identifiers
  - isLoading: Loading state from form service
  - isMobile: Device type detection for responsive behavior
  
  Navigation Logic:
  - Previous/Next buttons navigate between steps
  - Step indicators are clickable for completed steps
  - Submit button appears only on final step
  - Navigation respects step validation rules
  
  @author MacSafety Development Team
  @version 1.0
-->

<!-- 
  MULTI-STEP FORM LAYOUT
  =======================
  Main container that only renders when steps are defined.
  Provides the complete step-by-step form experience.
-->
<ion-content class="no-scroll">

  <!-- 
    STEPPED FORM CONTAINER
    =======================
    Main wrapper that contains all step-related functionality.
    Only renders when steps array exists and has content.
  -->
  <div *ngIf="steps && steps.length > 0" class="stepped-form">
    
    <!-- 
      LOADING OVERLAY
      ================
      Full-screen overlay displayed during form operations.
      Shows spinner and loading message from form service.
      Prevents user interaction while loading.
    -->
    <div *ngIf="isLoading()" class="loading-overlay">
      <ion-spinner name="crescent"></ion-spinner>
      <p>{{ loadingMessage() }}</p>
    </div>
  
    <!-- 
      FIXED HEADER SECTION
      =====================
      Always-visible header containing progress tracking.
      Fixed position ensures progress is always visible during scrolling.
    -->
  <div class="form-header-section" *ngIf="!isSingleStep()">

      <!-- 
        PROGRESS CONTAINER
        ===================
        Contains both the progress bar and step indicators.
        Provides visual feedback on form completion status.
      -->
  <div class="progress-container" *ngIf="!isSingleStep()">
        
        <!-- 
          PROGRESS BAR
          =============
          Visual representation of overall form completion.
          Width dynamically calculated based on current step progress.
          Uses percentage width binding for smooth animations.
        -->
  <div class="progress-bar" *ngIf="!isSingleStep()">
          <div 
            class="progress-fill" 
            [style.width.%]="getProgressPercentage()">
          </div>
        </div>
        
        <!-- 
          STEP INDICATORS
          ================
          Visual representation of each form step.
          Shows step icons, completion status, and allows navigation.
          Each indicator represents a step in the form workflow.
        -->
  <div class="step-indicators" *ngIf="!isSingleStep()">
          <div 
            *ngFor="let step of steps"
            class="step-indicator" 
            [class.active]="isStepActive(step.id)"
            [class.completed]="isStepCompleted(step.id)"
            [class.clickable]="canNavigateToStep(step.id)"
            (click)="goToStep(step.id)">
            
            <!-- 
              STEP CIRCLE
              ============
              Circular container for step icon or completion checkmark.
              Changes appearance based on step state (active/completed).
            -->
            <div class="step-circle">
              <!-- 
                STEP ICON
                ==========
                Shows step-specific icon when step is not completed.
                Icon represents the step's purpose or content.
              -->
              <ion-icon 
                *ngIf="!isStepCompleted(step.id)" 
                [name]="step.icon"
                class="step-icon">
              </ion-icon>
              
              <!-- 
                COMPLETION CHECKMARK
                =====================
                Shows checkmark icon when step is completed.
                Indicates successful completion of step requirements.
              -->
              <ion-icon 
                *ngIf="isStepCompleted(step.id)" 
                name="checkmark-outline" 
                class="check-icon">
              </ion-icon>
            </div>
            
            <!-- 
              STEP LABEL
              ===========
              Short title for each step displayed below the icon.
              Uses shortTitle for compact display in navigation.
            -->
            <span class="step-label">{{ step.shortTitle }}</span>
          </div>
        </div>
      </div>
    </div>
  
    <!-- 
      SCROLLABLE FORM CONTENT
      ========================
      Main content area where individual form components render their content.
      Uses ng-content projection to allow parent components to inject form fields.
      Scrollable to accommodate varying content lengths.
    -->
    <ion-content class="form-content-wrapper ion-safe-area-bottom ion-safe-area-top">
      <!-- 
        FORM CONTENT PROJECTION
        ========================
        Content selector that allows parent form components to inject their form fields.
        Parent templates use [formContent] attribute to project content here.
        This enables dynamic form content while maintaining consistent navigation.
      -->
      <ng-content select="[formContent]"></ng-content>
    </ion-content>

    <!-- 
      FIXED FOOTER NAVIGATION
      =========================
      Always-visible navigation controls at the bottom of the form.
      Contains previous/next buttons and submit button on final step.
      Fixed position ensures navigation is always accessible.
    -->
    <div class="nav-controls">
      
      <!-- 
        PREVIOUS BUTTON
        ================
        Navigation button to move to the previous step.
        Disabled on first step to prevent invalid navigation.
        Responsive design: icon-only on mobile, text+icon on desktop.
      -->
      <ion-button *ngIf="!isSingleStep()"
        [fill]="isMobile() ? 'icon-only' : 'clear'"
        color="primary"
        [style.visibility]="isFirstStep() ? 'hidden' : 'visible'"
        (click)="previousStep()">
        
        <!-- 
          PREVIOUS ARROW ICON
          ====================
          Left-pointing chevron indicating previous step navigation.
          Size adapts to device type for optimal touch targets.
        -->
        <ion-icon 
          [size]="isMobile() ? 'medium' : 'large'"
          color="primary"
          name="chevron-back-outline" 
          class="nav-arrow">
        </ion-icon>
        
        <!-- 
          PREVIOUS TEXT LABEL
          ====================
          Text label shown only on desktop devices.
          Hidden on mobile to save space and use icon-only design.
        -->
        {{isMobile() ? '' : 'Previous'}}
      </ion-button>
      
      <!-- 
        STEP COUNTER
        =============
        Displays current step position in the form workflow.
        Only shown when not on the final step.
        Provides context for user's progress through the form.
      -->
  <ion-text class="ion-text-center" color="primary" *ngIf="!isSingleStep() && !isLastStep()">
        Step {{ getCurrentStepNumber() }} of {{ getTotalSteps() }}
      </ion-text>
      
      <!-- 
        SUBMIT BUTTON
        ==============
        Final submission button that appears only on the last step.
        Solid green button with checkmark icon for clear action indication.
        Disabled state controlled by parent component's disabled input.
        Triggers form submission when clicked.
      -->
      <ion-button 
        *ngIf="isLastStep()"
        fill="solid" 
        color="success"
        (click)="onSubmit()"
        [disabled]="disabled"
        class="nav-button ion-text-center">
        
        <!-- 
          SUBMIT CHECKMARK ICON
          ======================
          Checkmark icon indicating successful completion and submission.
          Positioned at start of button for visual clarity.
        -->
        <ion-icon name="checkmark-outline" slot="start"></ion-icon>
        
        <!-- 
          SUBMIT TEXT
          ============
          Dynamic text that includes the form title.
          Provides clear indication of what will be submitted.
        -->
        Submit {{ title }}
      </ion-button>
      
      <!-- 
        NEXT BUTTON
        ============
        Navigation button to move to the next step.
        Disabled on last step to prevent invalid navigation.
        Responsive design: icon-only on mobile, text+icon on desktop.
        Triggers step validation before proceeding.
      -->
      <ion-button *ngIf="!isSingleStep()"
        [fill]="isMobile() ? 'icon-only' : 'clear'"
        color="primary"
        [style.visibility]="isLastStep() ? 'hidden' : 'visible'"
        (click)="nextStep()">
        
        <!-- 
          NEXT TEXT LABEL
          ================
          Text label shown only on desktop devices.
          Hidden on mobile to save space and use icon-only design.
        -->
        {{isMobile() ? '' : 'Next'}}
        
        <!-- 
          NEXT ARROW ICON
          ================
          Right-pointing chevron indicating next step navigation.
          Size adapts to device type for optimal touch targets.
        -->
        <ion-icon 
          [size]="isMobile() ? 'medium' : 'large'"
          color="primary"
          name="chevron-forward-outline" 
          class="nav-arrow">
        </ion-icon>
      </ion-button>
    
    </div>

  </div>
  
</ion-content>